/**
 * Changing your own password.
 *
 * Shown as a blocking screen when an administrator generated the current one, because a
 * password that has been written in a log or handed over must not survive its first use.
 */

import { useState } from "react";

import { ApiError } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import { Field, Notice } from "@/components/ui";

const MIN_LENGTH = 12;

export function ChangePassword({
  forced,
  onChanged,
  onCancel,
}: {
  forced: boolean;
  onChanged: () => void;
  onCancel?: () => void;
}) {
  const [current, setCurrent] = useState("");
  const [replacement, setReplacement] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  const mismatch = confirmation.length > 0 && confirmation !== replacement;
  const tooShort = replacement.length > 0 && replacement.length < MIN_LENGTH;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.changePassword(current, replacement);
      onChanged();
    } catch (caught) {
      setProblem(caught instanceof ApiError ? caught.message : (caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={submit}>
        <h1>{forced ? "Choose your own password" : "Change your password"}</h1>
        {forced && (
          <p className="signin__lede">
            The password you signed in with was set for you. Replace it before going any
            further.
          </p>
        )}

        {problem && (
          <div style={{ marginBottom: 16, textAlign: "left" }}>
            <Notice tone="danger">{problem}</Notice>
          </div>
        )}

        <div className="stack" style={{ gap: 12, textAlign: "left" }}>
          <Field label="Current password" required htmlFor="cp-current">
            <input
              id="cp-current"
              className="input"
              type="password"
              autoComplete="current-password"
              value={current}
              required
              autoFocus
              onChange={(event) => setCurrent(event.target.value)}
            />
          </Field>

          <Field
            label="New password"
            hint={`At least ${MIN_LENGTH} characters.`}
            error={tooShort ? `Use at least ${MIN_LENGTH} characters.` : undefined}
            required
            htmlFor="cp-new"
          >
            <input
              id="cp-new"
              className="input"
              type="password"
              autoComplete="new-password"
              value={replacement}
              required
              onChange={(event) => setReplacement(event.target.value)}
            />
          </Field>

          <Field
            label="Confirm new password"
            error={mismatch ? "These do not match." : undefined}
            required
            htmlFor="cp-confirm"
          >
            <input
              id="cp-confirm"
              className="input"
              type="password"
              autoComplete="new-password"
              value={confirmation}
              required
              onChange={(event) => setConfirmation(event.target.value)}
            />
          </Field>

          <button
            type="submit"
            className="button button--primary"
            style={{ width: "100%", alignSelf: "stretch" }}
            disabled={busy || mismatch || tooShort || !replacement}
          >
            {busy ? "Saving…" : "Change password"}
          </button>

          {!forced && onCancel && (
            <button type="button" className="button button--ghost" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
